import { Component, EventEmitter, OnInit } from '@angular/core';
import { CreditorStatusService } from '../creditor-status.service';
import { CreditorStatus } from '../creditor-status';
import { NotificationService } from '../../shared/notification.service';
import { AuthService } from '../../auth/auth.service';

@Component({
  selector   : 'app-creditor-status-list',
  templateUrl: 'list.component.html',
  styleUrls  : [],
})
export class CreditorStatusListComponent implements OnInit {
  public status: CreditorStatus;

  /**
   * A list of all the statuses currently loaded.
   *
   * @type {CreditorStatus[]}
   */
  public statuses: CreditorStatus[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of statuses.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of statuses that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of statuses that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a status has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public statusDeleted: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {CreditorStatusService} statusService
   * @param {NotificationService} notificationService
   */
  constructor(
    private statusService: CreditorStatusService,
    private notificationService: NotificationService,
    private authService: AuthService,
  )
  {
    this.statusService.loading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.statusService.totalPages.subscribe(totalPages => {
      this.totalPages = totalPages;
    });
    this.statusService.currentPage.subscribe(currentPage => {
      this.currentPage = currentPage;
    });
    this.statusService.totalResults.subscribe(totalResults => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the status list.
   */
  public ngOnInit(): void {
    this.loadStatuses(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * status list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadStatuses(pageNumber);
  }

  /**
   * Loads a list of statuses using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadStatuses(page?: number, limit?: number): void {
    const listStatuses = this.statusService
      .listStatuses(page, limit)
      .subscribe((statuses: CreditorStatus[]) => {
        this.statuses = statuses;
        listStatuses.unsubscribe();
      });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate status ID
   * to delete the status.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.statusService.deleteStatus(id).subscribe(
      deleted => {
        this.notificationService.success(
          `Status Deleted`,
          `Status with ID ${id} was deleted.`,
          5,
        );
        this.statusDeleted.next(true);
        this.onPageChanged(this.currentPage);
      },
      err => {
        this.notificationService.error(
          `Status Not Deleted`,
          err.error.message,
          10,
        );
        this.statusDeleted.next(false);
        this.onPageChanged(this.currentPage);
      },
    );
  }
}
